/**
 * exportRender.js — Carousel Composer export: render a slide to a JPEG/PNG blob
 * and save it.
 *
 * Browser-only at call time, import-safe in node (no DOM at module top level).
 * exportSourceRects() is the pure core the renderer uses, so seams and crops can
 * be verified without a canvas.
 *
 * Every destination rectangle is snapped to whole pixels by its edges, and so is
 * every source rectangle: strip i ends exactly where strip i+1 begins, which is
 * what keeps a panorama seam from showing at any size.
 *
 * Adjustments go through lib/carousel/pipeline.js — the same code the cell
 * previews are baked with, so the file matches the screen on every engine. It
 * replaced ctx.filter, which could not express highlights/shadows/whites/blacks
 * at all and was simply absent on some browsers: those exports saved ungraded
 * and nothing but a warning could be done about it. A cell whose look is neutral
 * still takes the plain draw path, untouched.
 */
import {
  cellFit, cellImgId, cellLookAt, cellPlacement, effectivePanoCell, formatSize, panoStrip,
  slideCells, slideGeom, srcRect,
} from './geometry.js';
import { bakedBox, drawBaked, isNeutralAdj, viewPhoto } from './look.js';
import { renderLook } from './pipeline.js';

// A turn is baked into an offscreen canvas before anything is measured, so every
// crop, seam and snap below still works on an axis-aligned photo. Capped so a
// 48MP original cannot allocate a vast canvas.
const MAX_BAKE_PX = 4500;

export function bakeFull(source, srcW, srcH, look) {
  const box = bakedBox(srcW, srcH, look);
  const scale = Math.min(1, MAX_BAKE_PX / Math.max(box.w, box.h));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(box.w * scale));
  c.height = Math.max(1, Math.round(box.h * scale));
  drawBaked(c.getContext('2d'), source, srcW, srcH, look, c.width, c.height);
  return c;
}

// Grades pixels that are ALREADY at w x h — the pipeline only fetches, it does
// not resample, so the scaling stays with createImageBitmap / drawImage where the
// quality is.
//
// A grade that cannot be produced FAILS THE SLIDE. renderLook() hands back null
// when its own fallback path could not read the pixels back, and the canvas it
// was drawing into holds the ungraded source at that point: saving that would be
// a file that quietly ignores every slider, which is the exact silent failure
// pipeline.js exists to end. The export sheet says Failed and offers a retry.
function graded(src, look, w, h) {
  const out = renderLook(src, look, { width: w, height: h });
  if (!out) throw new Error('could not grade this photo');
  return out;
}

export const EXPORT_LABELS = {
  ready: 'Ready', working: 'Rendering…', saved: 'Saved', skipped: 'Skipped', failed: 'Failed', empty: 'No photos',
};

export function saveButtonLabel(st) {
  return st === 'saved' ? 'Save again' : (st === 'skipped' || st === 'failed') ? 'Retry' : 'Save';
}

export function sanitizePrefix(value) {
  return String(value).trim().replace(/[^\w.-]+/g, '-');
}

export function exportCanvasSize(design, exportOpts) {
  const [W0, H0] = formatSize(design.fmt);
  const scale = exportOpts.qual / 1080;
  return { W: Math.round(W0 * scale), H: Math.round(H0 * scale), scale };
}

// "1440×1800"
export function outDims(design, exportOpts) {
  const { W, H } = exportCanvasSize(design, exportOpts);
  return W + '×' + H;
}

export function slideFilename(si, exportOpts) {
  return (exportOpts.prefix || 'slide') + '-' + String(si + 1).padStart(2, '0') + (exportOpts.type === 'png' ? '.png' : '.jpg');
}

export function slideHasPhotos(sl, panos, imgById) {
  return !!(sl && sl.cells.some((c, ci) => c && imgById(cellImgId(sl, ci, panos, imgById))));
}

function exportPlan(sl, W, H, ctx) {
  const scale = ctx.scale != null ? ctx.scale : W / formatSize(ctx.design.fmt)[0];
  const g = slideGeom(sl, ctx.design);
  const cells = slideCells(sl, W, H, g.pad * scale, g.gap * scale);
  const rects = cells.map((rc) => {
    const x0 = Math.round(rc.x), y0 = Math.round(rc.y);
    return { x: x0, y: y0, w: Math.max(1, Math.round(rc.x + rc.w) - x0), h: Math.max(1, Math.round(rc.y + rc.h) - y0) };
  });
  return { cells, rects };
}

// The crop is computed against the pixels actually decoded (natural), so a record
// whose w/h describe a smaller preview still exports the frame you composed. A pano
// part's crop is derived at the export cell's own aspect.
function cellSource(sl, ci, plan, ctx, natural) {
  const rc = plan.cells[ci];
  const id = cellImgId(sl, ci, ctx.panos, ctx.imgById);
  const im0 = id ? ctx.imgById(id) : null;
  if (!rc || !im0) return null;
  // the photo as its look leaves it: a turn changes the dimensions everything measures against
  const im = viewPhoto(im0, cellLookAt(sl, im0, ci));
  const size = natural || (ctx.naturalSize && ctx.naturalSize(im)) || { w: im.w, h: im.h };
  const nw = size.w, nh = size.h;
  const px = nw === im.w && nh === im.h ? im : { ...im, w: nw, h: nh };
  const lookup = (x) => (x === im.id ? px : ctx.imgById(x));
  const cell = sl.pano && ci === 0 ? effectivePanoCell(sl, ctx.panos, lookup, rc.w, rc.h) : sl.cells[ci];
  if (!cell) return null;
  if (cellFit(sl, sl.cells[ci]) === 'contain') {
    // Letterboxed: the whole photo is drawn into the cell, clipped if zoomed past it.
    const box = plan.rects[ci];
    const pl = cellPlacement(px, box.w, box.h, cell, 'contain');
    return {
      ci,
      imgId: im.id,
      mode: 'contain',
      dest: box,
      draw: { x: box.x + pl.dx, y: box.y + pl.dy, w: pl.dw, h: pl.dh },
    };
  }
  const r = srcRect(px, rc.w, rc.h, cell);
  let left = r.sx, right = r.sx + r.sw;
  const g = sl.pano && ci === 0 && ctx.panos ? ctx.panos[sl.pano.gid] : null;
  if (g && g.imgId === im.id) {
    // Both edges of every strip come from the same expression, a + k*sw, so strip
    // i's right edge is bit-identical to strip i+1's left edge before rounding.
    // Going through ox and srcRect instead can land either side of .5 and leave a
    // 1px overlap at the seam.
    const st = panoStrip(g, px, rc.w, rc.h);
    const a = g.start * st.slack;
    left = Math.min(nw - st.sw, Math.max(0, a + sl.pano.i * st.sw));
    right = Math.min(nw, Math.max(st.sw, a + (sl.pano.i + 1) * st.sw));
  }
  const sx = Math.max(0, Math.round(left));
  const sy = Math.max(0, Math.round(r.sy));
  const sw = Math.max(1, Math.min(nw - sx, Math.round(right) - sx));
  const sh = Math.max(1, Math.min(nh - sy, Math.round(r.sy + r.sh) - sy));
  return { ci, imgId: im.id, mode: 'fill', src: { sx, sy, sw, sh }, dest: plan.rects[ci] };
}

// Per cell (aligned with sl.cells; null for empty cells):
//   { ci, imgId, src:{ sx, sy, sw, sh }, dest:{ x, y, w, h } }
// W/H: output canvas px. ctx: { design, panos, imgById, scale?, naturalSize?(im) => {w,h} }
// scale defaults to W / 1080 (pad and gap are scaled by it).
export function exportSourceRects(sl, W, H, ctx) {
  const plan = exportPlan(sl, W, H, ctx);
  return sl.cells.map((_, ci) => cellSource(sl, ci, plan, ctx, null));
}

/** Pixel size of anything drawable: an ImageBitmap, an <img>, or a canvas. */
export function sourceSize(src) {
  if (!src) return { w: 0, h: 0 };
  return { w: src.naturalWidth || src.width || 0, h: src.naturalHeight || src.height || 0 };
}

/**
 * The original's pixels, fetched as a blob.
 *
 * Never an <img crossOrigin>: when the same delivery URL is already in the
 * browser cache from a plain view (a tile, the peek), some browsers refuse the
 * cross-origin request from cache, the draw silently does nothing, and the
 * export writes a slide of pure fill colour. Blob pixels are same-origin by the
 * time they reach the canvas, so they cannot be refused and cannot taint it.
 * Falls back to the portal's own route when a delivery URL has rotated.
 */
export async function loadFullPixels(im) {
  const get = async (u) => {
    const r = await fetch(u, { credentials: 'omit' });
    if (!r.ok) throw new Error('http ' + r.status);
    return r.blob();
  };
  let blob = null;
  try {
    blob = await get(im.url);
  } catch (err) {
    if (!im.fallbackUrl) throw err;
    blob = await get(im.fallbackUrl);
  }
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(blob); } catch { /* older engine */ }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.decoding = 'async';
      el.onload = () => res(el);
      el.onerror = () => rej(new Error('decode'));
      el.src = url;
      setTimeout(() => rej(new Error('timeout')), 20000);
    });
    img._url = url;
    return img;
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

export function releasePixels(src) {
  if (!src) return;
  if (src.close) src.close();
  if (src._url) URL.revokeObjectURL(src._url);
}

// ctx: { design, exportOpts, panos, imgById, loadFull? } -> Blob | null
export async function renderSlideToBlob(sl, ctx) {
  const { design, exportOpts } = ctx;
  const load = ctx.loadFull ? (im) => ctx.loadFull(im.url, im) : (im) => loadFullPixels(im);
  const { W, H, scale } = exportCanvasSize(design, exportOpts);
  const plan = exportPlan(sl, W, H, { ...ctx, scale });
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d');
  g.imageSmoothingQuality = 'high';
  g.fillStyle = design.bg;
  g.fillRect(0, 0, W, H);

  const opened = [];
  for (let i = 0; i < sl.cells.length; i++) {
    const id = cellImgId(sl, i, ctx.panos, ctx.imgById);
    const im = id ? ctx.imgById(id) : null;
    if (!im || !plan.cells[i]) continue;

    let full = null;
    try { full = await load(im); } catch { full = null; }
    const size = sourceSize(full);
    if (!full || !size.w) {
      // A cell that could not be fetched would export as a hole of fill colour.
      // Fail the slide instead, so the export sheet says so and you can retry.
      opened.forEach(releasePixels);
      throw new Error('could not load ' + (im.name || im.id));
    }
    opened.push(full);

    // Bake the turn first, then measure against the baked pixels.
    const look = cellLookAt(sl, im, i);
    let source = full, nw = size.w, nh = size.h;
    if (look.rot) {
      source = bakeFull(full, nw, nh, look);
      nw = source.width;
      nh = source.height;
    }
    const s = cellSource(sl, i, plan, ctx, { w: nw, h: nh });
    if (!s) continue;
    const box = s.dest;
    const plain = isNeutralAdj(look.adj);

    // A mat is fill colour AROUND this rectangle, laid down before its own photo
    // and over whatever it overlaps, so a small frame reads as a print on a print
    // rather than a collage. The rectangle is unchanged either way, so the crop,
    // the drag and the preview are untouched — SlideStage lays the same ring.
    const mat = Math.round(plan.cells[i].mat || 0);
    if (mat > 0) {
      g.fillStyle = design.bg;
      g.fillRect(box.x - mat, box.y - mat, box.w + 2 * mat, box.h + 2 * mat);
    }

    if (s.mode === 'contain') {
      g.save();
      g.beginPath();
      g.rect(box.x, box.y, box.w, box.h);
      g.clip();
      if (plain) {
        g.drawImage(source, 0, 0, nw, nh, s.draw.x, s.draw.y, s.draw.w, s.draw.h);
      } else {
        // Grade at the size it will be drawn at (never bigger than the source),
        // so the same pixels the preview graded are the pixels that land.
        const gw = Math.max(1, Math.min(nw, Math.round(s.draw.w)));
        const gh = Math.max(1, Math.round(gw * nh / nw));
        const fit = document.createElement('canvas');
        fit.width = gw;
        fit.height = gh;
        const fg = fit.getContext('2d');
        fg.imageSmoothingQuality = 'high';
        fg.drawImage(source, 0, 0, nw, nh, 0, 0, gw, gh);
        g.drawImage(graded(fit, look, gw, gh), 0, 0, gw, gh, s.draw.x, s.draw.y, s.draw.w, s.draw.h);
      }
      g.restore();
    } else {
      const { sx, sy, sw, sh } = s.src;
      let drawn = false;
      if (typeof createImageBitmap === 'function') {
        try {
          const bmp = await createImageBitmap(source, sx, sy, sw, sh, {
            resizeWidth: box.w, resizeHeight: box.h, resizeQuality: 'high',
          });
          g.drawImage(plain ? bmp : graded(bmp, look, box.w, box.h), box.x, box.y);
          if (bmp.close) bmp.close();
          drawn = true;
        } catch { /* older engine: fall back to the canvas scaler */ }
      }
      if (!drawn) {
        if (plain) {
          g.drawImage(source, sx, sy, sw, sh, box.x, box.y, box.w, box.h);
        } else {
          const fit = document.createElement('canvas');
          fit.width = box.w;
          fit.height = box.h;
          const fg = fit.getContext('2d');
          fg.imageSmoothingQuality = 'high';
          fg.drawImage(source, sx, sy, sw, sh, 0, 0, box.w, box.h);
          g.drawImage(graded(fit, look, box.w, box.h), box.x, box.y);
        }
      }
    }
    if (source !== full) { source.width = 0; source.height = 0; }   // release the baked canvas
  }
  // a long zip run holds nothing between slides
  opened.forEach(releasePixels);

  const type = exportOpts.type === 'png' ? 'image/png' : 'image/jpeg';
  return new Promise((res) => {
    const bail = setTimeout(() => res(null), 30000);
    canvas.toBlob((b) => { clearTimeout(bail); res(b); }, type, 0.96);
  });
}

export function canPickFolder() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

// The "Choose output folder" button. Returns true when a folder was chosen.
export async function pickOutputFolder(dirRef) {
  if (!canPickFolder()) return false;
  try {
    dirRef.current = await window.showDirectoryPicker({ mode: 'readwrite', id: 'carousel-out' });
    return true;
  } catch {
    return false;
  }
}

export function exportNoteText(dirRef, exportOpts) {
  const zip = exportOpts && exportOpts.bundle === 'zip';
  if (dirRef && dirRef.current) return 'Writing into “' + (dirRef.current.name || 'the chosen folder') + '”.';
  if (zip) return 'One zip goes to your browser’s downloads.';
  if (canPickFolder()) return 'Choose an output folder to skip the download prompts.';
  return 'Files go to your browser’s downloads.';
}

// Writes into the chosen folder when there is one (or pick:true asks for one),
// otherwise an ordinary browser download. Cancelling the picker is 'declined';
// any other folder failure forgets the folder and downloads instead.
export async function saveBlob(blob, filename, dirRef, { pick = false } = {}) {
  const ref = dirRef || { current: null };
  if (canPickFolder() && (ref.current || pick)) {
    try {
      if (!ref.current) ref.current = await window.showDirectoryPicker({ mode: 'readwrite', id: 'carousel-out' });
      const fh = await ref.current.getFileHandle(filename, { create: true });
      const w = await fh.createWritable();
      await w.write(blob);
      await w.close();
      return 'saved';
    } catch (err) {
      if (err && err.name === 'AbortError') return 'declined';
      ref.current = null;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return 'saved';
}

/**
 * Renders a run's slides and returns [{ si, name, blob }] for a zip, instead of
 * saving each one. onPhase(si, 'working'|'failed'|'empty') reports progress, and
 * stop() is checked between slides so "Stop after this one" still works. A slide
 * that fails is skipped rather than ending the run.
 */
export async function renderSlidesForZip(slides, list, ctx, onPhase, stop) {
  const files = [];
  for (const si of list) {
    if (stop && stop()) break;
    const sl = slides[si];
    if (!sl) continue;
    if (!slideHasPhotos(sl, ctx.panos, ctx.imgById)) {
      if (onPhase) onPhase(si, 'empty');
      continue;
    }
    if (onPhase) onPhase(si, 'working');
    let blob = null;
    try { blob = await renderSlideToBlob(sl, ctx); } catch { blob = null; }
    if (!blob) {
      if (onPhase) onPhase(si, 'failed');
      continue;
    }
    files.push({ si, name: slideFilename(si, ctx.exportOpts), blob });
  }
  return files;
}

// One slide, render to file: 'saved' | 'declined' | 'failed' | 'empty'.
// ctx: { design, exportOpts, panos, imgById, loadFull? }
export async function exportSlide(sl, si, ctx, dirRef, opts) {
  if (!slideHasPhotos(sl, ctx.panos, ctx.imgById)) return 'empty';
  let blob = null;
  try { blob = await renderSlideToBlob(sl, ctx); } catch { blob = null; }
  if (!blob) return 'failed';
  try { return await saveBlob(blob, slideFilename(si, ctx.exportOpts), dirRef, opts); } catch { return 'failed'; }
}
